/**
 * PerformanceMetrics Component
 * 
 * Trading KPIs and performance analytics
 * 
 * Version: 6.2.0
 */
import React from 'react'
import './PerformanceMetrics.css'

interface MetricData {
  label: string
  value: string | number
  subValue?: string
  change?: number
  status?: 'positive' | 'negative' | 'neutral'
  icon: string
}

interface PerformanceMetricsProps {
  metrics: MetricData[]
  variant?: 'grid' | 'list' | 'compact'
  showTrend?: boolean
  className?: string
}

// Default trading metrics
const defaultMetrics: MetricData[] = [
  { label: 'Win Rate', value: '68.5%', change: 2.3, status: 'positive', icon: '🎯' },
  { label: 'Sharpe Ratio', value: '2.14', change: 0.15, status: 'positive', icon: '📈' },
  { label: 'Max Drawdown', value: '-12.4%', change: -1.2, status: 'negative', icon: '📉' },
  { label: 'Profit Factor', value: '2.35', change: 0.08, status: 'positive', icon: '💹' },
  { label: 'Avg Trade', value: '₹1,250', change: 5.2, status: 'positive', icon: '💰' },
  { label: 'Total Trades', value: '156', icon: '🔢' },
  { label: 'Best Trade', value: '₹8,420', change: 12.5, status: 'positive', icon: '🏆' },
  { label: 'Worst Trade', value: '-₹2,150', change: -3.2, status: 'negative', icon: '⚠️' },
]

const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({
  metrics = defaultMetrics,
  variant = 'grid',
  showTrend = true,
  className = ''
}) => {
  const formatChange = (change: number) => {
    const sign = change >= 0 ? '+' : ''
    return `${sign}${change.toFixed(2)}%`
  }

  const getStatusClass = (status?: string, change?: number) => {
    if (status) return status
    if (change !== undefined) return change >= 0 ? 'positive' : 'negative'
    return 'neutral'
  }

  return (
    <div className={`performance-metrics ${variant} ${className}`}>
      {metrics.map((metric, index) => (
        <div 
          key={index}
          className={`metric-card ${getStatusClass(metric.status, metric.change)}`}
        >
          <div className="metric-icon">{metric.icon}</div>
          <div className="metric-content">
            <span className="metric-label">{metric.label}</span>
            <span className="metric-value">{metric.value}</span>
            {metric.subValue && (
              <span className="metric-subvalue">{metric.subValue}</span>
            )}
          </div>
          {showTrend && metric.change !== undefined && (
            <div className={`metric-trend ${metric.change >= 0 ? 'up' : 'down'}`}>
              <span className="trend-arrow">
                {metric.change >= 0 ? '▲' : '▼'}
              </span>
              <span className="trend-value">
                {formatChange(metric.change)}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// Risk/Reward Ratio Component
interface RiskRewardProps {
  risk: number
  reward: number
  ratio?: number
  className?: string
}

export const RiskRewardRatio: React.FC<RiskRewardProps> = ({
  risk,
  reward,
  ratio = reward / risk,
  className = ''
}) => {
  const getRatioClass = () => {
    if (ratio >= 2) return 'excellent'
    if (ratio >= 1.5) return 'good'
    if (ratio >= 1) return 'fair'
    return 'poor'
  }

  return (
    <div className={`risk-reward-ratio ${className}`}>
      <div className="rr-header">
        <span className="rr-title">Risk/Reward Ratio</span>
        <span className={`rr-badge ${getRatioClass()}`}>{ratio.toFixed(2)}:1</span>
      </div>
      <div className="rr-bar">
        <div className="rr-risk" style={{ width: `${(1 / (1 + ratio)) * 100}%` }}>
          <span>Risk</span>
        </div>
        <div className="rr-reward" style={{ width: `${(ratio / (1 + ratio)) * 100}%` }}>
          <span>Reward</span>
        </div>
      </div>
      <div className="rr-values">
        <span>Risk: ₹{risk.toLocaleString()}</span>
        <span>Reward: ₹{reward.toLocaleString()}</span>
      </div>
    </div>
  )
}

export default PerformanceMetrics